import * as React from 'react';
import {makeStyles, darken} from '@material-ui/core';
import {useQuery} from 'react-query';
import {toast} from 'react-hot-toast';
import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  addDays,
  format,
  subDays,
  differenceInMilliseconds,
  isValid,
} from 'date-fns';
import * as XLSX from 'xlsx';

import DataGrid from 'components/DataGrid';
import {p2gTransactionService} from 'services/p2gTransactionService';
import {ERROR_MESSAGE, FILE_EXPORT_SUCCESS} from 'utils/messages';
import {startOfDayTimestamp, getDate} from 'utils/Date';
import {DATE_FORMAT} from 'utils/regex';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {Typography, Grid, TextField, Select, MenuItem} from '@material-ui/core';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';

import Button from 'components/Button';

type StatusType = 'pending' | 'paid' | 'failed' | 'expired' | '';

type FilterInputs = {
  status: StatusType;
  search: string;
  createdFrom: Date;
  createdTo: Date;
};

// Add method to handle empty string
yup.addMethod(yup.string, 'stripEmptyString', function () {
  return this.transform(value => (value === '' ? undefined : value));
});

const filterSchema = yup.object().shape({
  status: yup.string(),
  search: yup.string(),

  createdFrom: yup
    .date()
    .typeError('Invalid date')
    .test('createdFromDateInvalid', 'Invalid date', function () {
      // validate Date
      const yupContext = this as any;
      if (typeof yupContext.originalValue === 'string') {
        return DATE_FORMAT.test(yupContext.originalValue);
      }
      return true;
    })
    .test(
      'createdToMin',
      'Created From should not be a future date',
      function () {
        // Min date validation
        if (isValid(this.parent.createdFrom)) {
          return (
            differenceInMilliseconds(new Date(), this.parent.createdFrom) >= 0
          );
        }
        return true;
      }
    ),

  createdTo: yup
    .date()
    .typeError('Invalid date')
    .test('createdFromDateInvalid', 'Invalid date', function () {
      // validate Date
      const yupContext = this as any;
      if (typeof yupContext.originalValue === 'string') {
        return DATE_FORMAT.test(yupContext.originalValue);
      }
      return true;
    })
    .test(
      'createdToMin',
      'Created To should not be a future date',
      function () {
        // Min date validation
        if (isValid(this.parent.createdTo)) {
          return (
            differenceInMilliseconds(new Date(), this.parent.createdTo) >= 0
          );
        }
        return true;
      }
    )
    .test(
      'createdToMin',
      'Created To cannot be before Created From',
      function () {
        // Min date validation
        if (isValid(this.parent.createdFrom)) {
          return (
            differenceInMilliseconds(
              this.parent.createdTo,
              this.parent.createdFrom
            ) >= 0
          );
        }
        return true;
      }
    ),
});

const statusOptions: {label: string; value: StatusType}[] = [
  {label: 'Pending', value: 'pending'},
  {label: 'Paid', value: 'paid'},
  {label: 'Failed', value: 'failed'},
  {label: 'Expired', value: 'expired'},
];

const getPaymentStatus = (order: any): string => {
  if (order.payment?.status) {
    const s = order.payment.status as string;
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  if (order.payment === null) return 'Pending';
  return '-';
};

const P2GTransaction: React.FC = () => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
  } = useForm<FilterInputs>({
    resolver: yupResolver(filterSchema),
    defaultValues: {
      status: '',
      search: '',
      createdFrom: subDays(new Date(startOfDayTimestamp(new Date())), 30),
      createdTo: new Date(startOfDayTimestamp(new Date())),
    },
  });

  const classes = useStyles();

  const {data, refetch, isFetching} = useQuery(
    ['p2gTransaction'],
    () => {
      const status = getValues('status') as StatusType;
      return p2gTransactionService.get({
        createdFrom: startOfDayTimestamp(getValues('createdFrom')),
        createdTo: startOfDayTimestamp(
          addDays(new Date(getValues('createdTo')), 1)
        ),
        ...(status ? {status} : {}),
      });
    },
    {retry: false}
  );

  // Filter orders client-side by search text (name, order id, email, service)
  const filteredOrders: any[] = React.useMemo(() => {
    if (!data?.orders?.length) return [];
    const q = (getValues('search') || '').toLowerCase().trim();
    if (!q) return data.orders;
    return data.orders.filter((order: any) => {
      const consumerName = `${order.consumerUser?.firstName || ''} ${
        order.consumerUser?.surName || ''
      }`.toLowerCase();
      const email = (order.consumerUser?.email || '').toLowerCase();
      const orderId = (order.p2gOrderId || order.id || '').toLowerCase();
      const service = (
        order.parcel?.parcelService?.Service?.Name || ''
      ).toLowerCase();
      return (
        consumerName.includes(q) ||
        email.includes(q) ||
        orderId.includes(q) ||
        service.includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.orders, watch('search')]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const resetFilters = () => {
    reset({
      status: '',
      search: '',
      createdFrom: subDays(new Date(startOfDayTimestamp(new Date())), 30),
      createdTo: new Date(startOfDayTimestamp(new Date())),
    });
  };

  const handleClearFilter = async (_event: React.MouseEvent) => {
    resetFilters();
    refetch({
      throwOnError: true,
      cancelRefetch: true,
    });
  };

  const onSubmit = async (_data: FilterInputs) => {
    refetch({
      throwOnError: true,
      cancelRefetch: true,
    });
  };

  const formatAddress = (addr: any): string => {
    if (!addr) return '-';
    if (typeof addr === 'string') return addr;
    return JSON.stringify(addr);
  };

  const formatOrderLines = (additionalInfo: any): string => {
    if (!additionalInfo) return '-';
    const lines: string[] = [];
    Object.keys(additionalInfo).forEach(key => {
      if (!isNaN(Number(key))) {
        const item = additionalInfo[key];
        lines.push(
          `Line ${Number(key) + 1}: OrderLineId=${
            item.OrderLineId || '-'
          }, ItemId=${item.ItemId || '-'}`
        );
      }
    });
    return lines.length ? lines.join(' | ') : '-';
  };

  const exportExcel = () => {
    try {
      if (!filteredOrders.length) return;

      let createXLSLFormatObj = [];

      /* XLS Head Columns */
      const xlsHeader = [
        // Group 1 — Order Info
        'P2G Order ID',
        'Internal Order ID',
        'Client Platform',
        'Payment Status',
        'Total Price (£)',
        'Total VAT (£)',
        'Total Ex-VAT (£)',
        'Created At',
        'Updated At',
        'Consumer Name',
        'Consumer Email',
        'Consumer Mobile',
        'Consumer Address',
        'Delivery Address',
        'Drop-off Address',
        'Parcel Height (cm)',
        'Parcel Width (cm)',
        'Parcel Length (cm)',
        'Parcel Weight (kg)',
        'Parcel Value (£)',
        'Parcel Content',
        'Service Name',
        'Courier Name',
        'Collection Type',
        'Delivery Type',
        'Estimated Delivery',
        'Service Price (£)',
        'Currency',
        // Group 6 — Feedback
        'Feedback Score',
        'Feedback Comment',
        // Group 7 — Order Line Items
        'Order Line Items',
      ];
      createXLSLFormatObj.push(xlsHeader);

      for (const order of filteredOrders) {
        const parcel = order.parcel;
        const service = parcel?.parcelService;
        const serviceInfo = service?.Service;
        const consumer = order.consumerUser;
        const additionalInfo = order.additionalInfo;

        let innerRowData = [];

        // Group 1 — Order Info
        innerRowData.push(order.p2gOrderId || '-');
        innerRowData.push(order.id || '-');
        innerRowData.push(order.clientPlatform?.type || '-');
        innerRowData.push(getPaymentStatus(order));
        innerRowData.push(
          order.totalPrice?.TotalPrice != null
            ? order.totalPrice.TotalPrice.toFixed(2)
            : '-'
        );
        innerRowData.push(
          order.totalPrice?.TotalVat != null
            ? order.totalPrice.TotalVat.toFixed(2)
            : '-'
        );
        innerRowData.push(
          order.totalPrice?.TotalPriceExVat != null
            ? order.totalPrice.TotalPriceExVat.toFixed(2)
            : '-'
        );
        innerRowData.push(
          order.createdAt
            ? format(new Date(order.createdAt), 'dd-MMM-yyyy HH:mm:ss')
            : '-'
        );
        innerRowData.push(
          order.updatedAt
            ? format(new Date(order.updatedAt), 'dd-MMM-yyyy HH:mm:ss')
            : '-'
        );

        // Group 2 — Consumer Info
        innerRowData.push(
          consumer
            ? `${consumer.firstName || ''} ${consumer.surName || ''}`.trim()
            : '-'
        );
        innerRowData.push(consumer?.email || '-');
        innerRowData.push(
          consumer
            ? `${consumer.countryCode || ''}${
                consumer.mobileNumber || ''
              }`.trim() || '-'
            : '-'
        );

        // Consumer Address — CollectionAddress from first item in orderRequest.Items
        const collectionAddr =
          order.orderRequest?.Items?.[0]?.CollectionAddress;
        innerRowData.push(
          collectionAddr
            ? [
                collectionAddr.Property,
                collectionAddr.Street,
                collectionAddr.Locality,
                collectionAddr.Town,
                collectionAddr.Postcode,
                collectionAddr.CountryIsoCode,
              ]
                .filter(Boolean)
                .join(', ')
            : '-'
        );

        // Group 3 — Delivery Address
        innerRowData.push(formatAddress(order.deliveryAddress));

        // Group 4 — Drop-off Address
        innerRowData.push(formatAddress(order.dropOffAddress));

        // Group 5 — Parcel Details
        innerRowData.push(parcel?.height || '-');
        innerRowData.push(parcel?.width || '-');
        innerRowData.push(parcel?.length || '-');
        innerRowData.push(parcel?.weight || '-');
        innerRowData.push(parcel?.parcelValue || '-');
        innerRowData.push(parcel?.content || '-');
        innerRowData.push(serviceInfo?.Name || '-');
        innerRowData.push(serviceInfo?.CourierName || '-');
        innerRowData.push(serviceInfo?.CollectionType || '-');
        innerRowData.push(serviceInfo?.DeliveryType || '-');
        innerRowData.push(
          service?.EstimatedDeliveryDate
            ? format(new Date(service.EstimatedDeliveryDate), 'dd-MMM-yyyy')
            : '-'
        );
        innerRowData.push(
          service?.TotalPrice != null ? service.TotalPrice.toFixed(2) : '-'
        );
        innerRowData.push(service?.CurrencyCode || '-');

        // Group 6 — Feedback
        innerRowData.push(additionalInfo?.feedback?.liked_wa_returns || '-');
        innerRowData.push(additionalInfo?.feedback?.additional_comments || '-');

        // Group 7 — Order Line Items
        innerRowData.push(formatOrderLines(additionalInfo));

        createXLSLFormatObj.push(innerRowData);
      }

      /* File Name */
      const fileName = `P2GTransactions_${format(
        new Date(),
        'dd-MMM-yyyy'
      )}.xlsx`;
      /* Sheet Name — max 31 chars */
      const wsName = 'P2G Transactions';

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(createXLSLFormatObj);

      /* Add worksheet to workbook */
      XLSX.utils.book_append_sheet(wb, ws, wsName);

      /* Write workbook and Download */
      XLSX.writeFile(wb, fileName);
      toast.success(FILE_EXPORT_SUCCESS, {duration: 3000});
    } catch (error) {
      console.log(error);
      toast.error(ERROR_MESSAGE);
    }
  };

  return (
    <div>
      <Typography component={'h1'} className={classes.title}>
        P2G Transaction
      </Typography>
      <Button
        variant="contained"
        color="primary"
        minWidth="0px"
        className={classes.exportButton}
        onClick={exportExcel}
        disabled={!filteredOrders.length}
      >
        Export Data
      </Button>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.filterContainer} spacing={2}>
          <Grid item xs={9}>
            <Typography className={classes.filterText} component={'h2'}>
              Filter
            </Typography>
          </Grid>
          <Grid
            container
            xs={3}
            spacing={2}
            alignItems="center"
            justify="flex-end"
          >
            <Grid item>
              <Button
                variant="outlined"
                color="inherit"
                fullWidth
                minWidth="0px"
                onClick={handleClearFilter}
                className={classes.cancelButton}
              >
                Clear
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                minWidth="0px"
                fullWidth
              >
                {'Apply'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid container className={classes.filterOptionContainer} spacing={2}>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Search</Typography>
            <Controller
              name="search"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <TextField
                  variant="outlined"
                  type="text"
                  placeholder="Name, order ID, email, service..."
                  fullWidth
                  margin="dense"
                  value={value}
                  onChange={onChange}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>
              Payment Status
            </Typography>
            <Controller
              name="status"
              control={control}
              render={({field: {value, onChange}}) => (
                <Select
                  value={value}
                  onChange={onChange}
                  displayEmpty
                  fullWidth
                  margin="dense"
                  variant="outlined"
                  className={classes.select}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {statusOptions.map(opt => (
                    <MenuItem value={opt.value} key={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Grid>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Created From</Typography>
            <Controller
              name="createdFrom"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="dd-MMM-yyyy"
                    className={classes.datePicker}
                    disableFuture
                    allowSameDateSelection
                    value={value}
                    onChange={onChange}
                    maxDate={getDate(watch('createdTo'))}
                    renderInput={(params: any) => (
                      <TextField
                        variant="outlined"
                        type="text"
                        placeholder="Add Date"
                        fullWidth
                        margin="dense"
                        value={value}
                        onChange={onChange}
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
          <Grid item xs={3} className={classes.formElement}>
            <Typography className={classes.formLabel}>Created To</Typography>
            <Controller
              name="createdTo"
              control={control}
              render={({field: {value, onChange}, fieldState: {error}}) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    inputFormat="dd-MMM-yyyy"
                    className={classes.datePicker}
                    disableFuture
                    allowSameDateSelection
                    minDate={getDate(watch('createdFrom'))}
                    value={value}
                    onChange={onChange}
                    renderInput={(params: any) => (
                      <TextField
                        variant="outlined"
                        type="text"
                        placeholder="Add Date"
                        fullWidth
                        margin="dense"
                        value={value}
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            />
          </Grid>
        </Grid>
      </form>
      <div className={classes.tableContainer}>
        <DataGrid
          columns={[
            {
              Header: 'P2G Order ID',
              accessor: (row: any) => row.p2gOrderId || '-',
            },
            {
              Header: 'Consumer Name',
              accessor: (row: any) =>
                row.consumerUser
                  ? `${row.consumerUser.firstName || ''} ${
                      row.consumerUser.surName || ''
                    }`.trim()
                  : '-',
            },
            {
              Header: 'Consumer Email',
              accessor: (row: any) => row.consumerUser?.email || '-',
            },
            {
              Header: 'Service',
              accessor: (row: any) =>
                row.parcel?.parcelService?.Service?.Name || '-',
            },
            {
              Header: 'Total Price',
              accessor: (row: any) =>
                row.totalPrice?.TotalPrice != null
                  ? `£ ${row.totalPrice.TotalPrice.toFixed(2)}`
                  : '-',
            },
            {
              Header: 'Payment Status',
              accessor: (row: any) => getPaymentStatus(row),
            },
            {
              Header: 'Feedback Score',
              accessor: (row: any) =>
                row.additionalInfo?.feedback?.liked_wa_returns || '-',
            },
            {
              Header: 'Feedback Comment',
              accessor: (row: any) =>
                row.additionalInfo?.feedback?.additional_comments || '-',
            },
            {
              Header: 'Created At',
              accessor: (row: any) =>
                row.createdAt
                  ? format(new Date(row.createdAt), 'dd-MMM-yyyy HH:mm')
                  : '-',
            },
          ]}
          data={filteredOrders}
          recordCount={isFetching ? 0 : filteredOrders.length}
          defaultPageSize={5}
          isLoading={isFetching}
          onPaginationChange={() => {}}
          noDataMessage={'No items available'}
        />
      </div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: 26,
    lineHeight: '40px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
      lineHeight: '28px',
    },
    fontWeight: theme.typography.fontWeightBold,
  },
  exportButton: {
    float: 'right',
    marginBottom: '20px',
  },
  tabs: {
    marginBottom: '43px',
  },
  tab: {
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: 14,
    lineHeight: '20px',
  },
  filterContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: '5px 5px 0 0',
    padding: '12px 16px 15px',
    margin: 0,
    border: '1px solid',
    borderBottom: '0px',
    borderColor: theme.palette.grey[300],
  },
  filterOptionContainer: {
    width: '100%',
    background: '#fff',
    borderRadius: '0 0 5px 5px',
    padding: '12px 16px 15px',
    margin: 0,
    marginBottom: theme.spacing(3),
    border: '1px solid',
    borderColor: theme.palette.grey[300],
  },
  filterText: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '21px',
    lineHeight: '32px',
  },
  formElement: {},
  formLabel: {
    fontSize: 16,
    lineHeight: '28px',
  },
  hash: {
    backgroundColor: theme.palette.grey[400],
  },
  adornmentTextField: {
    width: '100%',
    '& span': {
      backgroundColor: theme.palette.grey[400],
    },
  },
  select: {
    marginTop: '8px',
    marginBottom: '4px',
  },
  datePicker: {
    backgroundColor: 'pink',
    '&& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: 'red !important',
      '&:hover': {
        backgroundColor: 'red !important',
      },
      '&:focus': {
        backgroundColor: 'red !important',
      },
    },
  },
  textFieldSkeleton: {
    width: '100%',
    height: 40,
    borderRadius: 4,
    marginTop: 8,
    marginBottom: 4,
  },
  tableContainer: {
    width: '100%',
    maxWidth: 'calc(100vw - 399px)',
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'calc(100vw - 122px)',
    },
    // Override DataGrid TableCell wrapping from parent
    '& .MuiTableCell-root': {
      width: 'none',
      overflowWrap: 'break-word',
      wordBreak: 'ellipsis',
      whiteSpace: 'normal',
    },
  },
  name: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },
  dashboardText: {
    fontSize: 14,
    lineHeight: '20px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
      lineHeight: '14px',
    },
    fontWeight: theme.typography.fontWeightRegular,
    marginBottom: theme.spacing(1),
  },
  subjectText: {
    fontSize: 21,
    lineHeight: '32px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
      lineHeight: '24px',
    },
    fontWeight: theme.typography.fontWeightBold,
  },
  subjectInput: {
    margin: 0,
  },
  cancelButton: {
    color: theme.palette.grey[500],
  },
  editButton: {
    minWidth: '0px',
    width: 'auto',
    padding: 9,
    fontSize: 18,
    backgroundColor: theme.palette.primary.light,
    '&:hover': {
      backgroundColor: darken(theme.palette.primary.light, 0.1),
    },
  },
  updateButton: {
    minWidth: '0px',
    width: 'auto',
  },
  toolbar: {
    borderRadius: '5px',
  },
  editorError: {
    fontSize: '0.75rem',
    color: theme.palette.error.main,
    margin: '4px 14px 0px',
  },
  content: {
    backgroundColor: '#fff',
    height: 'calc(100vh - 220px)',
    minHeight: '300px',
    padding: 30,
  },
}));

export default P2GTransaction;
